/**
 * VueTableau.js — Vue Tableau du Planning d'Actions
 * Design System SEIZO | Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 *
 * Fonctionnalités :
 *   - Colonnes configurables (choix des attributs à afficher)
 *   - Tri par colonne (clic sur header)
 *   - Filtres rapides (statut, priorité)
 *   - Clic sur une ligne → ouvre TacheForm en tiroir droit
 *   - Inline-edit du statut via select
 *   - Persistance des colonnes choisies dans localStorage
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../stores/Context';
import styles from './VueTableau.module.scss';
import { getPlanningPriority, PLANNING_PRIORITIES } from './planningPriority';

// ── Constantes ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'pa_tableau_colonnes';

const ALL_COLUMNS = [
    { id: 'titre',        label: 'Action',      width: 260, fixed: true  },
    { id: 'statut',       label: 'Statut',      width: 130, fixed: false },
    { id: 'priorite',     label: 'Priorité',    width: 110, fixed: false },
    { id: 'responsable',  label: 'Pilote',      width: 140, fixed: false },
    { id: 'date_debut',   label: 'Début',       width: 110, fixed: false },
    { id: 'date_estimee', label: 'Échéance',    width: 110, fixed: false },
    { id: 'pourcentage',  label: 'Avancement',  width: 130, fixed: false },
    { id: 'description',  label: 'Description', width: 200, fixed: false },
];

const DEFAULT_VISIBLE = ['titre', 'statut', 'priorite', 'responsable', 'date_estimee', 'pourcentage'];

// ── Icônes SVG ────────────────────────────────────────────────────────────────

const IcoKanban = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="11" rx="1"/>
    </svg>
);
const IcoCalendar = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);
const IcoTableau = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <line x1="3" y1="9" x2="21" y2="9"/>
        <line x1="3" y1="15" x2="21" y2="15"/>
        <line x1="9" y1="9" x2="9" y2="21"/>
    </svg>
);
const IcoSettings = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
);
const IcoPlus = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);
const IcoSort = ({ dir }) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        {dir === 'asc'  && <polyline points="6 15 12 9 18 15"/>}
        {dir === 'desc' && <polyline points="6 9 12 15 18 9"/>}
        {!dir           && <><polyline points="6 9 12 3 18 9"/><polyline points="6 15 12 21 18 15" opacity="0.3"/></>}
    </svg>
);
const IcoColumns = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/>
    </svg>
);
const IcoCheck = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const isOverdue = (d) => {
    if (!d) return false;
    return new Date(d) < new Date() && new Date(d).toDateString() !== new Date().toDateString();
};

const loadVisibleCols = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : DEFAULT_VISIBLE;
    } catch { return DEFAULT_VISIBLE; }
};

const saveVisibleCols = (cols) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cols)); } catch {}
};

// ── Cellule de rendu ──────────────────────────────────────────────────────────

const CellValue = ({ colId, tache, statuts, onStatutChange, onProgressChange }) => {
    switch (colId) {
        case 'titre':
            return (
                <span className={styles.cellTitre}>{tache.titre || '—'}</span>
            );
        case 'statut': {
            const s = tache.statut;
            return (
                <select
                    className={styles.statutSelect}
                    value={tache.statut?.id || ''}
                    style={{ '--statut-color': s?.couleur || '#A8B0B3' }}
                    onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); onStatutChange(tache.id, e.target.value); }}
                >
                    {statuts.map(st => (
                        <option key={st.id} value={st.id}>{st.libelle}</option>
                    ))}
                </select>
            );
        }
        case 'priorite': {
            const p = getPlanningPriority(tache.priorite);
            return (
                <span className={styles.prioriteBadge} style={{ '--p-color': p.color }}>
                    {p.label}
                </span>
            );
        }
        case 'responsable': {
            const r = tache.responsable;
            if (!r) return <span className={styles.cellMuted}>—</span>;
            const initials = `${r.prenom?.[0] || ''}${r.nom?.[0] || ''}`.toUpperCase();
            return (
                <span className={styles.responsableCell}>
                    <span className={styles.avatar}>{initials}</span>
                    <span className={styles.responsableNom}>{r.prenom} {r.nom}</span>
                </span>
            );
        }
        case 'date_debut':
            return <span className={styles.cellDate}>{formatDate(tache.date_debut)}</span>;
        case 'date_estimee':
            return (
                <span className={`${styles.cellDate} ${isOverdue(tache.date_estimee) ? styles.cellDateOverdue : ''}`}>
                    {formatDate(tache.date_estimee)}
                </span>
            );
        case 'pourcentage':
            return (
                <div className={styles.progressCell}>
                    <div className={styles.progressBar}>
                        <div
                            className={styles.progressFill}
                            style={{ width: `${tache.pourcentage || 0}%` }}
                        />
                    </div>
                    <input
                        key={`${tache.id}-${tache.pourcentage || 0}`}
                        className={styles.progressInput}
                        type="number"
                        min="0"
                        max="100"
                        step="5"
                        defaultValue={tache.pourcentage || 0}
                        aria-label={`Avancement de ${tache.titre}`}
                        onClick={event => event.stopPropagation()}
                        onKeyDown={event => {
                            if (event.key === 'Enter') event.currentTarget.blur();
                        }}
                        onBlur={event => onProgressChange(tache.id, event.target.value)}
                    />
                    <span className={styles.progressSuffix}>%</span>
                </div>
            );
        case 'description':
            return (
                <span className={styles.cellDescription}>
                    {tache.description || '—'}
                </span>
            );
        default:
            return <span className={styles.cellMuted}>—</span>;
    }
};

// ── Panneau de configuration des colonnes ─────────────────────────────────────

const ColonnePicker = ({ visibleCols, onChange, onClose }) => (
    <div className={styles.colPicker}>
        <div className={styles.colPickerHeader}>
            <span>Colonnes affichées</span>
            <button className={styles.colPickerClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.colPickerList}>
            {ALL_COLUMNS.map(col => {
                const checked = visibleCols.includes(col.id);
                return (
                    <label key={col.id} className={`${styles.colPickerItem} ${col.fixed ? styles.colPickerItemFixed : ''}`}>
                        <span className={`${styles.colPickerCheck} ${checked ? styles.colPickerCheckOn : ''}`}>
                            {checked && <IcoCheck />}
                        </span>
                        <span className={styles.colPickerLabel}>{col.label}</span>
                        {col.fixed && <span className={styles.colPickerFixed}>fixe</span>}
                        {!col.fixed && (
                            <input
                                type="checkbox"
                                checked={checked}
                                style={{ display: 'none' }}
                                onChange={() => {
                                    const next = checked
                                        ? visibleCols.filter(c => c !== col.id)
                                        : [...visibleCols, col.id];
                                    onChange(next);
                                }}
                            />
                        )}
                    </label>
                );
            })}
        </div>
    </div>
);

// ── Composant principal ───────────────────────────────────────────────────────

const VueTableau = observer(() => {
    const { PlanningActionsStore } = useStores();
    // Déclenche le chargement des tâches si le store est vide
    useEffect(() => {
        if (PlanningActionsStore.taches.length === 0 && PlanningActionsStore.idPlanning) {
            PlanningActionsStore.recupTaches('planning');
        }
    }, [PlanningActionsStore, PlanningActionsStore.idPlanning]);
    const { structure, taches, statuts, viewModalFormAction } = PlanningActionsStore;

    const [visibleCols, setVisibleCols]   = useState(loadVisibleCols);
    const [showPicker, setShowPicker]     = useState(false);
    const [sortCol, setSortCol]           = useState(null);
    const [sortDir, setSortDir]           = useState('asc');
    const [filterStatut, setFilterStatut] = useState('');
    const [filterPrio, setFilterPrio]     = useState('');

    // Colonnes actives dans l'ordre de ALL_COLUMNS
    const activeCols = useMemo(
        () => ALL_COLUMNS.filter(c => visibleCols.includes(c.id)),
        [visibleCols]
    );

    // Tri
    const handleSort = (colId) => {
        if (sortCol === colId) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortCol(colId);
            setSortDir('asc');
        }
    };

    // Données filtrées + triées
    const rows = useMemo(() => {
        let list = [...(taches || [])];
        if (filterStatut) list = list.filter(t => String(t.statut?.id) === filterStatut);
        if (filterPrio)   list = list.filter(t => String(t.priorite) === filterPrio);
        if (sortCol) {
            list.sort((a, b) => {
                let va = a[sortCol], vb = b[sortCol];
                if (sortCol === 'statut')      { va = a.statut?.libelle || ''; vb = b.statut?.libelle || ''; }
                if (sortCol === 'responsable') { va = a.responsable?.nom || ''; vb = b.responsable?.nom || ''; }
                if (va == null) va = '';
                if (vb == null) vb = '';
                const cmp = String(va).localeCompare(String(vb), 'fr', { numeric: true });
                return sortDir === 'asc' ? cmp : -cmp;
            });
        }
        return list;
    }, [taches, filterStatut, filterPrio, sortCol, sortDir]);

    const handleRowClick = useCallback((tache) => {
        PlanningActionsStore.openModalFormAction(
            tache.colonne_id,
            tache.sous_colonne_id,
            tache.ligne_id,
            tache.id
        );
    }, [PlanningActionsStore]);

    const handleStatutChange = useCallback((tacheId, statutId) => {
        PlanningActionsStore.updateStatutTache(tacheId, statutId)
            .catch(error => console.error('Impossible de mettre à jour le statut:', error));
    }, [PlanningActionsStore]);

    const handleProgressChange = useCallback((tacheId, value) => {
        const task = PlanningActionsStore.taches.find(tache => String(tache.id) === String(tacheId));
        if (!task || Number(value) === Number(task.pourcentage || 0)) return;
        PlanningActionsStore.updateTaskProgress(tacheId, value)
            .catch(error => console.error('Impossible de mettre à jour l’avancement:', error));
    }, [PlanningActionsStore]);

    const handleNewTask = () => {
        PlanningActionsStore.viewModalFormAction = 'creation';
    };

    const handleColsChange = (next) => {
        // Toujours garder 'titre'
        const withFixed = next.includes('titre') ? next : ['titre', ...next];
        setVisibleCols(withFixed);
        saveVisibleCols(withFixed);
    };

    const handleClose = () => {
        PlanningActionsStore.viewModalFormAction = null;
    };

    // ── Drag & drop depuis la boîte de réception ─────────────────────────────
    const handleDragOver = (e) => {
        if (e.dataTransfer.types.includes('application/reception-tache')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        }
    };
    const handleDrop = async (e) => {
        if (!e.dataTransfer.types.includes('application/reception-tache')) return;
        e.preventDefault();
        const raw = e.dataTransfer.getData('application/reception-tache');
        if (!raw) return;
        try {
            const tache = JSON.parse(raw);
            const today = new Date().toISOString().split('T')[0];
            await PlanningActionsStore.accepterTacheReceptionVersCalendrier(tache.id, today);
        } catch (err) {
            console.error('VueTableau drop error:', err);
        }
    };

    if (!structure) return null;

    return (
        <div className={styles.root} onDragOver={handleDragOver} onDrop={handleDrop}>

            {/* ── Barre d'outils ──────────────────────────────────────── */}
            <div className={styles.toolbar}>
                {/* Filtres */}
                <div className={styles.filters}>
                    <select
                        className={styles.filterSelect}
                        value={filterStatut}
                        onChange={e => setFilterStatut(e.target.value)}
                    >
                        <option value="">Tous les statuts</option>
                        {statuts.map(s => (
                            <option key={s.id} value={s.id}>{s.libelle}</option>
                        ))}
                    </select>
                    <select
                        className={styles.filterSelect}
                        value={filterPrio}
                        onChange={e => setFilterPrio(e.target.value)}
                    >
                        <option value="">Toutes les priorités</option>
                        {PLANNING_PRIORITIES.filter(({ value }) => value > 0).map(priority => (
                            <option key={priority.value} value={priority.value}>{priority.label}</option>
                        ))}
                    </select>
                </div>

                {/* Compteur + config colonnes */}
                <div className={styles.toolbarRight}>
                    <span className={styles.rowCount}>{rows.length} action{rows.length !== 1 ? 's' : ''}</span>
                    <div className={styles.colPickerWrapper}>
                        <button
                            className={`${styles.colPickerBtn} ${showPicker ? styles.colPickerBtnActive : ''}`}
                            onClick={() => setShowPicker(v => !v)}
                            title="Configurer les colonnes"
                        >
                            <IcoColumns /> Colonnes
                        </button>
                        {showPicker && (
                            <ColonnePicker
                                visibleCols={visibleCols}
                                onChange={handleColsChange}
                                onClose={() => setShowPicker(false)}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tableau ─────────────────────────────────────────────── */}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {activeCols.map(col => (
                                <th
                                    key={col.id}
                                    className={`${styles.th} ${sortCol === col.id ? styles.thSorted : ''}`}
                                    style={{ width: col.width, minWidth: col.width }}
                                    onClick={() => handleSort(col.id)}
                                >
                                    <span className={styles.thLabel}>{col.label}</span>
                                    <span className={styles.thSort}>
                                        <IcoSort dir={sortCol === col.id ? sortDir : null} />
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={activeCols.length} className={styles.emptyRow}>
                                    Aucune action ne correspond aux filtres
                                </td>
                            </tr>
                        ) : (
                            rows.map(tache => (
                                <tr
                                    key={tache.id}
                                    className={styles.tr}
                                    onClick={() => handleRowClick(tache)}
                                >
                                    {activeCols.map(col => (
                                        <td key={col.id} className={styles.td}>
                                            <CellValue
                                                colId={col.id}
                                                tache={tache}
                                                statuts={statuts}
                                                onStatutChange={handleStatutChange}
                                                onProgressChange={handleProgressChange}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

        </div>
    );
});

export default VueTableau;
